import { action, computed, extendObservable, observable, runInAction } from 'mobx';
import autobind from 'autobind-decorator';
import { ScoreUtil } from 'utils/scoreUtil';
import { timeUtils } from 'utils/timeUtil';
import AbstractScoreAudioPrompt from '../../../store/audio';

@autobind
class TennisModel extends AbstractScoreAudioPrompt {
  @observable
  goalEvent = {
    location: '',
    isActive: false,
  };
  @observable specials = [];

  constructor(data) {
    super();
    extendObservable(this, data);
  }

  @computed
  get quarterArr() {
    return [
      { key: '1set', value: 1 },
      { key: '2set', value: 2 },
      { key: '3set', value: 3 },
      { key: '4set', value: 4 },
      { key: '5set', value: 5 },
    ];
  }

  @computed
  get getStartDateTime() {
    return timeUtils.timeFormatHourMinute(this.startDatetime);
  }

  @computed
  get getTeamData() {
    const teamData = ScoreUtil.getTeamScore(this, this.gameStatus, this.sportsType, this.period);
    teamData.home.score = this.homeTotalScore;
    teamData.away.score = this.awayTotalScore;
    return teamData;
  }

  @computed
  get getTypeData() {
    return {};
  }

  @computed
  get getTracker() {
    const gameStatus = this.gameStatus;

    if (gameStatus === 'IN_PROGRESS' || gameStatus === 'BREAK_TIME' || gameStatus === 'PAUSE') {
      return { status: 'progress', class: '' };
    } else if (
      gameStatus === 'FINAL' ||
      gameStatus === 'CUT' ||
      gameStatus === 'POSTPONED' ||
      gameStatus === 'PENDING' ||
      gameStatus === 'CANCEL' ||
      gameStatus === 'DELAY' ||
      gameStatus === 'SUSPENDED'
    ) {
      return { status: 'final', class: 'close' };
    } else {
      // READY
      return { status: 'ready', class: 'wait' };
    }
  }

  @computed
  get availableBroadcast() {
    const { broadcast, teams } = this;
    let initBroadCast = {
      score: {},
      playText: '',
      locationType: null,
      eventType: '',
      displayTime: '00:00',
      teamName: '',
    };

    if (!!broadcast) {
      initBroadCast.score = !!broadcast.score ? broadcast.score : initBroadCast.score;
      initBroadCast.playText = !!broadcast.playText ? broadcast.playText : initBroadCast.playText;
      initBroadCast.locationType = !!broadcast.locationType ? broadcast.locationType : initBroadCast.locationType;
      initBroadCast.eventType = !!broadcast.eventType ? broadcast.eventType : initBroadCast.eventType;
      initBroadCast.displayTime = !!broadcast.displayTime ? broadcast.displayTime : initBroadCast.displayTime;
      if (!!initBroadCast.locationType) {
        if (initBroadCast.locationType === 'HOME') {
          initBroadCast.teamName = teams.home.name;
        } else if (initBroadCast.locationType === 'AWAY') {
          initBroadCast.teamName = teams.away.name;
        }
      }
    }

    return initBroadCast;
  }

  @computed
  get getSetScore() {
    return this.teams.home.periodData.map((item1) => {
      const item2 = this.teams.away.periodData.find((i) => i.sets === item1.sets);
      let winner = 'draw';
      if (item1.gameScore > item2.gameScore) winner = 'home';
      else if (item1.gameScore < item2.gameScore) winner = 'away';

      return {
        sets: item1.sets,
        home_gameScore: item1.gameScore,
        away_gameScore: item2 ? item2.gameScore : null,
        winner: winner,
      };
    });
  }

  @computed
  get winnerTeam() {
    const { teams } = this;
    const homeScore = this.homeTotalScore;
    const awayScore = this.awayTotalScore;
    let team = 'draw';
    let teamData = null;

    if (homeScore > awayScore) {
      team = 'home';
      teamData = teams.home;
    } else if (homeScore < awayScore) {
      team = 'away';
      teamData = teams.away;
    } else if (homeScore === awayScore) {
      team = 'draw';
      teamData = {
        imgPath: '',
        name: '',
      };
    }

    return {
      activeScore: team,
      team: teamData,
      difference: Math.abs(homeScore - awayScore),
    };
  }

  @computed
  get homeTotalScore() {
    let total = 0;
    if(this.manualSetScore?.home != null && this.manualSetScore?.away != null){
      total = this.manualSetScore.home;
    } else {
      const setScores = this.getSetScore;
      setScores.forEach((data) => {
        if (this.gameStatus === 'IN_PROGRESS' && this.period === data.sets) return;
        data.winner === 'home' && total++;
      });
    }

    return total;
  }

  @computed
  get awayTotalScore() {
    let total = 0;

    if(this.manualSetScore?.home != null && this.manualSetScore?.away != null){
      total = this.manualSetScore.away;
    } else {
      const setScores = this.getSetScore;
      setScores.forEach((data) => {
        if (this.gameStatus === 'IN_PROGRESS' && this.period === data.sets) return;
        data.winner === 'away' && total++;
      });
    }

    return total;
  }

  @computed
  get summaryScore() {
    const { homeTotalScore, awayTotalScore } = this;

    return homeTotalScore + awayTotalScore;
  }

  @computed
  get oddsData() {
    const { odds } = this;
    let result = {
      domestic: {
        home: null,
        homeOption: null,
        away: null,
        awayOption: null,
      },
      international: {
        home: null,
        homeOption: null,
        away: null,
        awayOption: null,
      },
    };

    result.domestic = ScoreUtil.getScoreOdds({
      oddsArr: odds.domesticWinLoseOdds,
      handiArr: odds.domesticHandicapOdds,
      uoArr: odds.domesticUnderOverOdds,
    });

    result.international = ScoreUtil.getScoreOdds({
      oddsArr: odds.internationalWinLoseOdds,
      handiArr: odds.internationalHandicapOdds,
      uoArr: odds.internationalUnderOverOdds,
    });

    return result;
  }

  @computed
  get sortedHomeScores() {
    const { teams } = this;
    const { home } = teams;

    return home.periodData.sort((a, b) => a.sets - b.sets);
  }

  @computed
  get sortedAwayScores() {
    const { teams } = this;
    const { away } = teams;

    return away.periodData.sort((a, b) => a.sets - b.sets);
  }

  @computed
  get statusText() {
    const { gameStatus, period } = this;
    let statusText = '';

    if (gameStatus === 'CANCEL') {
      statusText = '취소';
    } else if (gameStatus === 'DELAY' || gameStatus === 'POSTPONED') {
      statusText = '연기';
    } else if (gameStatus === 'BREAK_TIME') {
      statusText = 'HT';
    } else if (gameStatus === 'PAUSE') {
      statusText = '중지';
    } else if (gameStatus === 'PENDING' || gameStatus === 'CUT') {
      statusText = '중단';
    } else if (gameStatus === 'READY') {
      statusText = '시작전';
    } else if (gameStatus === 'FINAL') {
      statusText = '종료';
    } else if (gameStatus === 'IN_PROGRESS') {
      statusText = `${period}set`;
    }

    return statusText;
  }

  @computed
  get betbleButtonVisible() {
    const { betbleUri, getTracker } = this;

    return !!betbleUri && getTracker.status !== 'final';
  }

  /**********************************************************
   * actions
   **********************************************************/
  @action
  updateGame(data) {
    for (const [key, value] of Object.entries(data)) {
      if (key === 'teams') {
        this.teams = {
          ...this.teams,
          ...value,
          home: {
            ...this.teams.home,
            ...value.home,
          },
          away: {
            ...this.teams.away,
            ...value.away,
          },
        };
      } else {
        this[key] = value;
      }
      // console.log(`${key}: ${value}`);
    }
  }

  @action
  updatePeriodData(data, isFavoriteActivePrompt, isFavorite) {
    const { home, away } = this.getTeamData;
    const total = home.score + away.score;

    const next = {
      home: data.home.reduce((acc, cur) => acc + cur.score, 0),
      away: data.away.reduce((acc, cur) => acc + cur.score, 0),
    };
    const nextTotal = next.home + next.away;

    if (nextTotal > total) {
      let location = 'home';

      if (next.home > home.score) {
        location = 'home';
      } else if (next.away > away.score) {
        location = 'away';
      }

      this.setGoalEvent(location);
    }

    if (nextTotal !== +total) {
      const { league, statusText, id } = this;

      let location = 'home';

      if (next.home !== home.score) {
        location = 'home';
      } else if (next.away !== away.score) {
        location = 'away';
      }

      if (isFavoriteActivePrompt) {
        if (isFavorite) {
          this.addPrompt(
            id,
            { name: home.name, score: next.home },
            { name: away.name, score: next.away },
            location,
            league,
            statusText
          );
        }
      } else {
        this.addPrompt(
          id,
          { name: home.name, score: next.home },
          { name: away.name, score: next.away },
          location,
          league,
          statusText
        );
      }
    }

    this.teams = {
      ...this.teams,
      home: {
        ...this.teams.home,
        periodData: data.home,
      },
      away: {
        ...this.teams.away,
        periodData: data.away,
      },
    };
  }

  @action
  setGoalEvent(location) {
    if (!this.goalEvent.isActive) {
      runInAction(() => {
        this.goalEvent.isActive = true;
        this.goalEvent.location = location;

        setTimeout(() => {
          runInAction(() => {
            this.goalEvent = {
              location: '',
              isActive: false,
            };
          });
        }, 5000);
      });
    }
  }

  @computed
  get getSpecials() {
    return this.specials;
  }

  @action
  updateSpecial(data) {
    this.specials = data['tennisSetSpecialData'];
  }

  @action
  updateLastBroadcast(data) {
    this.broadcast = data;
  }
}

export default TennisModel;
